import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import axios from "axios";
import { API_BASE_URL } from "../../apiConfig";

export const AuthContext = createContext<{
  token: string | null;
  setToken: (token: string | null) => void;
  login: () => void;
  logout: () => void;
  isAuthenticated: boolean;
  setIsAuthenticated: (value: boolean) => void;
  refreshToken: () => Promise<string | null>;
} | null>(null);

type AuthProviderProps = {
  children: ReactNode;
};

const AUTH_API_URL = `${API_BASE_URL}/auth`;

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [token, setToken] = useState<string | null>(
    localStorage.getItem("authToken")
  );
  const [isAuthenticated, setIsAuthenticated] = useState(!!token);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      if (window.location.pathname === "/login") {
        setIsLoading(false);
        return;
      }
      try {
        const response = await axios.post(
          `${AUTH_API_URL}/token`,
          {},
          { withCredentials: true, timeout: 5000 }
        );
        const newToken = response.data.token;
        if (newToken) {
          setToken(newToken);
          localStorage.setItem("authToken", newToken);
          setIsAuthenticated(true);
        } else {
          window.location.href = "/login";
        }
      } catch (error) {
        window.location.href = "/login";
      } finally {
        setIsLoading(false);
      }
    };

    if (!token) {
      checkAuth();
    } else {
      setIsLoading(false);
    }
  }, [token]);

  const login = () => {
    window.location.href = `${AUTH_API_URL}/login`;
  };

  const logout = async () => {
    try {
      await axios.post(`${AUTH_API_URL}/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setToken(null);
      localStorage.removeItem("authToken");
      setIsAuthenticated(false);
    }
  };

  function debounce<T extends (...args: any[]) => Promise<string | null>>(
    func: T,
    wait: number
  ): (...args: Parameters<T>) => Promise<string | null> {
    let timeout: ReturnType<typeof setTimeout>;
    return (...args: Parameters<T>): Promise<string | null> => {
      clearTimeout(timeout);
      return new Promise((resolve) => {
        timeout = setTimeout(() => resolve(func(...args)), wait);
      });
    };
  }

  const refreshToken = useCallback(
    debounce(async () => {
      if (isRefreshing) return null;
      setIsRefreshing(true);
      try {
        const response = await axios.post(
          `${AUTH_API_URL}/token`,
          {},
          { withCredentials: true }
        );
        const newToken = response.data.token;
        if (newToken) {
          setToken(newToken);
          localStorage.setItem("authToken", newToken);
          setIsAuthenticated(true);
          return newToken;
        } else {
          throw new Error("No token received");
        }
      } catch (error) {
        console.error("Token refresh failed:", error);
        logout();
        return null;
      } finally {
        setIsRefreshing(false);
      }
    }, 500),
    [isRefreshing]
  );

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (error.response?.status === 401) {
          originalRequest._retry = true;
          const newToken = await refreshToken();
          if (newToken) {
            originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
            return axios(originalRequest);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, [refreshToken]);

  return (
    <AuthContext.Provider
      value={{
        token,
        setToken,
        login,
        logout,
        isAuthenticated,
        setIsAuthenticated,
        refreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
